// Filter specifications for the platform-wide Screener panel.
//
// Two filter shapes (per the build spec):
//   1. CATEGORICAL — multi-select pickers backed by the /screener/facets
//      endpoint. Values are populated from the data, not hard-coded, so
//      new markets adding values appear automatically.
//        country, region, subsegment, ownershipType, qualityTier
//   2. RANGE — two-handle sliders with sentinel "no cap" semantics at the
//
// All numeric readouts route through ../format.ts so negatives render
// in accounting brackets (the workspace-wide convention).
//      bounds (the existing screener pattern, preserved):
//        turnover, netAssets, employees, yearsActive, revenueConfidence
//
// Active filter = anything other than the default state. Counts shown
// on the FilterModal trigger chip use isFilterActive.

import { SPECIALTY_TOKENS } from '@hc/shared';
import { formatCount as fmtCount, formatMoneyTrim } from '../format.js';
import { REVENUE_STOPS } from './revenue-scale.js';
import { CURRENCY_SYMBOL, convertCurrency, type GlobalCurrency } from '../currency.js';

export type CategoricalFilterKey =
  | 'country'
  | 'region'
  | 'subsegment'
  | 'clinicalSpecialism'
  | 'backing'
  | 'ownershipType'
  | 'tenure'
  | 'qualityTier';

export const CATEGORICAL_FILTER_KEYS: readonly CategoricalFilterKey[] = [
  'country',
  'region',
  // Sub-segment holds BOTH the canonical-17 and their fine specialty children
  // (nursing home / residential care / …) in one hierarchical dropdown — the
  // mixed set is split into subsegment[] + specialty[] at query-build time.
  'subsegment',
  // Clinical specialism (blueprint X-factor) refines subsegment — the "find
  // dementia care homes / vet-equine / orthodontics" axis.
  'clinicalSpecialism',
  // Backing (independence axis) sits above Ownership type (control axis): the
  // two compose — Backing = Independent gives the acquirable universe, Ownership
  // type refines it by control structure.
  'backing',
  'ownershipType',
  // Premises tenure (who holds the building) — GB + FR property layer.
  'tenure',
  'qualityTier',
] as const;

export type CategoricalFilters = Readonly<Record<CategoricalFilterKey, ReadonlySet<string>>>;

export function defaultCategoricalFilters(): CategoricalFilters {
  return {
    country: new Set(),
    region: new Set(),
    subsegment: new Set(),
    clinicalSpecialism: new Set(),
    backing: new Set(),
    ownershipType: new Set(),
    tenure: new Set(),
    qualityTier: new Set(),
  };
}

// --- range filters ----------------------------------------------------

export type RangeFilterKey =
  | 'turnover'
  | 'netAssets'
  | 'employees'
  | 'revenueConfidence'
  | 'originationSignal'
  | 'sellerReadiness';

export type RangeUnit = 'money' | 'count' | 'years' | 'p_value' | 'score';

export interface RangeSpec {
  key: RangeFilterKey;
  labelKey: string;
  min: number;
  max: number;
  step: number;
  unit: RangeUnit;
  /** Currency the bounds + the value sent to the API are denominated in
   *  (money units only). Defaults to GBP (the UK-native legacy); the revenue
   *  axis is EUR so it lines up with the €150k platform floor + turnover_eur. */
  baseCurrency?: GlobalCurrency;
  /** Optional discrete value ladder (ascending, base-currency units). When
   *  set, the slider runs over stop INDICES — each stop gets equal track
   *  width regardless of value — so the axis is non-linear: packing many small
   *  stops and few large ones gives small companies most of the track. The
   *  first/last stop must equal min/max (the "no-cap" sentinels). Overrides
   *  `step`. */
  stops?: readonly number[];
}

// Bounds — at the bound, filter is "no cap" on that edge.
//   Revenue estimate            €150k .. >€100m  (€150k = platform floor; the
//                               lower bound starts at the floor, not €0, since
//                               nothing below it is in scope — see §3)
//   Net assets / Equity        <-£10m .. >£50m  (negative equity is real signal)
//   Employees                   0 .. >500
//   Years active                0 .. >50y
//   Revenue confidence (P)      P0 .. P100
export const RANGE_SPECS: readonly RangeSpec[] = [
  {
    key: 'turnover',
    labelKey: 'screener.filterModal.range.revenueEstimate',
    min: 150_000,
    max: 100_000_000,
    step: 1, // overridden by `stops` (index slider)
    unit: 'money',
    baseCurrency: 'EUR',
    // Non-linear axis — shared REVENUE_STOPS (dense under €2m). Each stop =
    // equal track width; first/last = min/max.
    stops: REVENUE_STOPS,
  },
  {
    key: 'netAssets',
    labelKey: 'screener.filterModal.range.netAssets',
    min: -10_000_000,
    max: 50_000_000,
    step: 1_000_000,
    unit: 'money',
  },
  {
    key: 'employees',
    labelKey: 'screener.filterModal.range.employees',
    min: 0,
    max: 500,
    step: 10,
    unit: 'count',
  },
  {
    key: 'revenueConfidence',
    labelKey: 'screener.filterModal.range.revenueConfidence',
    min: 0,
    max: 100,
    step: 5,
    unit: 'p_value',
  },
  {
    key: 'originationSignal',
    labelKey: 'screener.filterModal.range.originationSignal',
    min: 0,
    max: 100,
    step: 5,
    unit: 'score',
  },
  {
    key: 'sellerReadiness',
    labelKey: 'screener.filterModal.range.sellerReadiness',
    min: 0,
    max: 100,
    step: 5,
    unit: 'score',
  },
];

export const RANGE_SPEC_BY_KEY: Readonly<Record<RangeFilterKey, RangeSpec>> = Object.fromEntries(
  RANGE_SPECS.map((s) => [s.key, s]),
) as Readonly<Record<RangeFilterKey, RangeSpec>>;

export type RangeValue = readonly [number, number];
export type RangeFilters = Readonly<Record<RangeFilterKey, RangeValue>>;

export function defaultRangeFilters(): RangeFilters {
  return Object.fromEntries(
    RANGE_SPECS.map((s) => [s.key, [s.min, s.max] as RangeValue]),
  ) as unknown as RangeFilters;
}

export function isRangeActive(spec: RangeSpec, value: RangeValue): boolean {
  return value[0] !== spec.min || value[1] !== spec.max;
}

export function countActiveRanges(ranges: RangeFilters): number {
  let n = 0;
  for (const s of RANGE_SPECS) {
    if (isRangeActive(s, ranges[s.key])) n += 1;
  }
  return n;
}

export function countActiveCategoricals(c: CategoricalFilters): number {
  let n = 0;
  for (const k of CATEGORICAL_FILTER_KEYS) {
    if (c[k].size > 0) n += 1;
  }
  return n;
}

// Format a money/count/years/p value for slider readouts. All routes
// through ../format.ts so compact suffixes, decimal places, and
// negative-bracket convention stay uniform with the rest of the app.
//
// Money sliders carry a base currency (spec.baseCurrency, default GBP — the
// UK-native legacy; the revenue axis is EUR to match the €150k floor). When a
// display currency is supplied (header selector), readouts are FX-rebased into
// it for display ONLY; the underlying bounds + the value sent to the API stay
// in the base currency.
export function formatRangeValue(
  spec: RangeSpec,
  value: number,
  displayCurrency?: GlobalCurrency,
): string {
  switch (spec.unit) {
    case 'count':
      return fmtCount(value);
    case 'years':
      return value.toLocaleString('en-GB');
    case 'score':
      return value.toLocaleString('en-GB');
    case 'p_value':
      return `P${value}`;
    case 'money': {
      const base = spec.baseCurrency ?? 'GBP';
      const ccy = displayCurrency ?? base;
      // Trim trailing zeros: whole values read clean (€150K, €100M) while
      // ladder values keep precision (€1.25M, €7.5M).
      return formatMoneyTrim(convertCurrency(value, base, ccy), { currency: CURRENCY_SYMBOL[ccy] });
    }
  }
}

export function formatRangeBound(
  spec: RangeSpec,
  value: number,
  edge: 'min' | 'max',
  displayCurrency?: GlobalCurrency,
): string {
  const f = formatRangeValue(spec, value, displayCurrency);
  // ">" / "<" mean "no cap on that edge". P-values and the 0–100 scores are
  // hard scales, so 100 is the real ceiling, not ">100".
  if (spec.unit === 'p_value' || spec.unit === 'score') return f;
  if (edge === 'max' && value === spec.max) return `>${f}`;
  if (edge === 'min' && value === spec.min && spec.min < 0) return `<${f}`;
  return f;
}

export function summarizeRange(
  spec: RangeSpec,
  value: RangeValue,
  displayCurrency?: GlobalCurrency,
): string {
  if (!isRangeActive(spec, value)) return 'All';
  const [lo, hi] = value;
  const lowerUnbounded = lo === spec.min;
  const upperUnbounded = hi === spec.max;
  const fmt = (v: number) => formatRangeValue(spec, v, displayCurrency);
  if (lowerUnbounded && !upperUnbounded) return `≤ ${fmt(hi)}`;
  if (!lowerUnbounded && upperUnbounded) return `≥ ${fmt(lo)}`;
  return `${fmt(lo)} – ${fmt(hi)}`;
}

// --- ScreenerQuery mapping --------------------------------------------

// Compose the on-wire ScreenerQuery from the panel's local filter state.
// Only active edges of each range are sent (filter spec design — omitted
// param means "no bound"). Categorical filters send the selected set
// when non-empty; the API ignores empty arrays.
import type { ScreenerQuery } from '../api/screener-client.js';

export function buildScreenerQuery(
  categoricals: CategoricalFilters,
  ranges: RangeFilters,
  q: string,
  sortBy: ScreenerQuery['sortBy'],
  sortDir: ScreenerQuery['sortDir'],
): ScreenerQuery {
  const out: ScreenerQuery = {};
  if (q.trim()) out.q = q.trim();
  if (categoricals.country.size > 0) out.country = Array.from(categoricals.country);
  if (categoricals.region.size > 0) out.region = Array.from(categoricals.region);
  // Sub-segment holds a mix of canonical-17 labels and fine specialty tokens
  // (one hierarchical dropdown) — split back into the two API axes.
  if (categoricals.subsegment.size > 0) {
    const subs: string[] = [];
    const specs: string[] = [];
    for (const v of categoricals.subsegment) (SPECIALTY_TOKENS.has(v) ? specs : subs).push(v);
    if (subs.length) out.subsegment = subs;
    if (specs.length) out.specialty = specs;
  }
  if (categoricals.clinicalSpecialism.size > 0)
    out.clinicalSpecialism = Array.from(categoricals.clinicalSpecialism);
  if (categoricals.backing.size > 0) out.backing = Array.from(categoricals.backing);
  if (categoricals.ownershipType.size > 0)
    out.ownershipType = Array.from(categoricals.ownershipType);
  if (categoricals.tenure.size > 0) out.tenure = Array.from(categoricals.tenure);
  if (categoricals.qualityTier.size > 0) out.qualityTier = Array.from(categoricals.qualityTier);

  for (const spec of RANGE_SPECS) {
    const [lo, hi] = ranges[spec.key];
    const lowerActive = lo !== spec.min;
    const upperActive = hi !== spec.max;
    if (!lowerActive && !upperActive) continue;
    const minKey = `${spec.key}Min` as const;
    const maxKey = `${spec.key}Max` as const;
    if (lowerActive) (out as Record<string, unknown>)[minKey] = lo;
    if (upperActive) (out as Record<string, unknown>)[maxKey] = hi;
  }
  if (sortBy) out.sortBy = sortBy;
  if (sortDir) out.sortDir = sortDir;
  return out;
}
